import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { TransactionTime } from "./transaction-details/TransactionTime";
import { TransactionAmount } from "./transaction-details/TransactionAmount";
import { TransactionSignature } from "./TransactionSignature";
import { getProductName } from "./ProductUtils";
import { useOwnerData } from "@/hooks/useOwnerData";
import { getTransactionTypeLabel } from "./TransactionTypeUtils";
import { TransactionActions } from "./transaction-details/TransactionActions";
import { Badge } from "../ui/badge";

interface TransactionRowProps {
  transaction: any;
  showType?: boolean;
}

export const TransactionRow = ({ transaction, showType = true }: TransactionRowProps) => {
  const { data: ownerData } = useOwnerData();
  const isCommerceOrHardware = ownerData?.business_types?.name === "Commerce" || 
                              ownerData?.business_types?.name === "Quincaillerie";
  
  // Calculate the actual amount considering promotions
  const getDisplayAmount = () => {
    if (transaction.promotion_id) {
      return transaction.total_amount / transaction.quantity; // Use the already discounted unit price
    }
    return transaction.unit_price;
  };

  // Add promotion badge if applicable
  const getPromotionBadge = () => {
    if (transaction.promotion_id && transaction.original_price) {
      const discountAmount = transaction.original_price - (transaction.total_amount / transaction.quantity);
      const discountPercentage = ((discountAmount / transaction.original_price) * 100).toFixed(0);
      return (
        <Badge variant="outline" className="ml-2 bg-green-100 text-green-800 border-green-200">
          Promotion -{discountPercentage}%
        </Badge>
      );
    }
    return null;
  };
  
  if (isCommerceOrHardware) {
    return (
      <tr className="hover:bg-gray-50">
        <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
          <TransactionTime date={transaction.created_at} />
        </td>
        <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm">
          <div className="flex items-center">
            <TransactionAmount transaction={transaction} />
          </div>
        </td>
        <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
          {transaction.quantity}
        </td>
        <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
          {transaction.stock_items?.data?.Race || "N/A"}
        </td>
        <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
          <div className="flex items-center gap-2">
            {getProductName(transaction.stock_items)}
            {getPromotionBadge()}
          </div>
        </td>
        <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
          <TransactionSignature signature={transaction.digital_signature} />
        </td>
      </tr>
    );
  }

  // Return original layout for other business types
  const type = transaction.is_fully_paid === false ? "credit_sale" : transaction.transaction_type;
  const race = transaction.stock_items?.data?.Race || "N/A";
  
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
        <TransactionTime date={transaction.created_at} />
      </td>
      {showType && (
        <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
          {getTransactionTypeLabel(type)}
        </td>
      )}
      <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm">
        <div className="flex items-center">
          <TransactionAmount transaction={transaction} />
        </div>
      </td>
      <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
        {transaction.quantity}
      </td>
      <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
        {race}
      </td>
      <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
        <div className="flex items-center gap-2">
          {getProductName(transaction.stock_items)}
          {getPromotionBadge()}
        </div>
      </td>
      <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
        <TransactionSignature signature={transaction.digital_signature} />
      </td>
      <td className="px-3 md:px-6 py-2 md:py-4 whitespace-nowrap text-right text-xs md:text-sm">
        <TransactionActions transaction={transaction} />
      </td>
    </tr>
  );
};